/**
 * Microsoft + Google sign-in, the shared half (firm-model spec §5.2, R24): which providers
 * a Supabase project has on, what they're called, and the one message the add-in's sign-in
 * dialog hands the taskpane (`Office.context.ui.messageParent`). Used by the plugin (the
 * taskpane and its dialog page) and the web panel. Pure: no Office.js, no Supabase.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";

/** Supabase provider ids for the two OAuth options. */
export type OAuthProvider = "azure" | "google";

export const PROVIDER_LABEL: Record<OAuthProvider, string> = {
  azure: "Microsoft",
  google: "Google",
};

/** Providers switched on in the Supabase project, from its public `/auth/v1/settings` body,
 * Microsoft first. Anything unexpected reads as none: the email code always works. */
export function enabledProviders(settings: unknown): OAuthProvider[] {
  const external = (settings as { external?: Record<string, unknown> } | null)?.external;
  if (!external || typeof external !== "object") return [];
  const order: OAuthProvider[] = ["azure", "google"];
  return order.filter((p) => external[p] === true);
}

/** The dialog page's path on the add-in host (webpack emits it next to taskpane.html). */
export const AUTH_DIALOG_PATH = "/auth-dialog.html";

export type AuthDialogMessage =
  | { kind: "session"; accessToken: string; refreshToken: string }
  | { kind: "error"; reason: string };

const authDialogMessageSchema = z.union([
  z.object({ kind: z.literal("session"), accessToken: z.string().min(1), refreshToken: z.string().min(1) }),
  z.object({ kind: z.literal("error"), reason: z.string() }),
]);

expectParity<z.infer<typeof authDialogMessageSchema>, AuthDialogMessage>(true);

export function encodeAuthDialogMessage(message: AuthDialogMessage): string {
  return JSON.stringify(message);
}

/** Anything malformed reads as an error: the taskpane never adopts a half session. */
export function parseAuthDialogMessage(raw: string): AuthDialogMessage {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { kind: "error", reason: "malformed" };
  }
  const parsed = authDialogMessageSchema.safeParse(json);
  return parsed.success ? (parsed.data as AuthDialogMessage) : { kind: "error", reason: "malformed" };
}
